import { createTheme } from '@mui/material/styles';
import { pickersSectionListClasses } from '@mui/x-date-pickers/PickersSectionList';
import { pickersOutlinedInputClasses } from '@mui/x-date-pickers/PickersTextField';

import { colors } from './colors';
import { gradients } from './gradients';

const inputPadding = '10px 12px';

const optionStyles = {
  padding: '10px 12px',
  '&:hover, &.Mui-focusVisible': {
    backgroundColor: colors.white.hover,
  },
  '&:not(:first-child)': {
    borderTop: `1px solid ${colors.gray.divider}`,
  },
  '&.Mui-selected': {
    backgroundColor: colors.white.active,
    '&:hover': {
      backgroundColor: colors.white.hover,
    },
  },
};

const theme = createTheme({
  cssVariables: true,
  palette: {
    ...colors,
    gradients,
    text: {
      primary: colors.black.primary,
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 640,
      md: 768,
      lg: 1024,
      xl: 1280,
    },
  },
  typography: {
    fontSize: 14,
    fontFamily: 'Plus Jakarta Sans',
    caption: {
      fontSize: '12px',
      lineHeight: '16px',
    },
    h2: {
      fontWeight: 700,
      fontSize: '20px',
      lineHeight: '32px',
    },
    h1: {
      fontWeight: 700,
      fontSize: '40px',
      lineHeight: '48px',
    },
    h3: {
      fontWeight: 700,
      fontSize: '14px',
      lineHeight: '20px',
    },
    body2: {
      fontWeight: 500,
      fontSize: '14px',
      lineHeight: '20px',
    },
    body1: {
      fontWeight: 500,
      fontSize: '14px',
      lineHeight: '20px',
    },
  },
  components: {
    MuiMenuItem: {
      styleOverrides: {
        root: optionStyles,
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          padding: '0px',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        startIcon: {
          marginLeft: 0,
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        input: {
          padding: inputPadding,
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        input: {
          padding: inputPadding,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        icon: {
          top: 8,
          right: 12,
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          '&.MuiMenu-list': {
            padding: 0,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          width: 4,
          height: 4,
          background: gradients.accentGradient.primary.primary,
        },
      },
    },
    // @ts-ignore
    MuiPickersDay: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            background: gradients.accentGradient.primary.primary,
          },
        },
      },
    },
    // @ts-ignore
    MuiYearCalendar: {
      styleOverrides: {
        root: {
          '& .MuiYearCalendar-button.Mui-selected': {
            background: gradients.accentGradient.primary.primary,
          },
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          gap: '4px',
          width: '100%',
          padding: '12px',
          marginLeft: 'unset',
          marginRight: 'unset',
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          '&.Mui-error': {
            fontSize: '10px',
            margin: '4px 0 0',
            lineHeight: '16px',
            color: colors.red.primary.primary,
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          padding: '12px',
          fontSize: '12px',
          lineHeight: '16px',
          borderRadius: '8px',
          color: colors.black.primary,
          background: colors.white.primary,
          border: `1.5px solid ${colors.gray.primary}`,
          boxShadow: '0 6px 12px 0 rgba(var(--accent-shadow-rgb), 0.05)',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme }) => ({
          margin: 16,
          borderRadius: '16px',
          maxHeight: 'calc(100% - 32px)',
          background: colors.white.primary,
          boxShadow: '0 6px 12px 0 rgba(var(--accent-shadow-rgb), 0.05)',
          [theme.breakpoints.up('md')]: {
            margin: 24,
            maxHeight: 'calc(100% - 48px)',
          },
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          '&.MuiMenu-paper': {
            padding: '1px',
            display: 'flex',
            boxShadow: 'none',
            overflow: 'hidden',
            borderRadius: '16px',
            background: gradients.accentGradient.primary.primary,
            '.MuiList-root': {
              width: '100%',
              maxHeight: '100%',
              overflowY: 'auto',
              borderRadius: '15px',
              background: colors.white.primary,
            },
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInputBase-input': {
            height: 18,
            borderRadius: '15px',
            padding: inputPadding,
            background: colors.white.primary,

            '&::placeholder': {
              opacity: 1,
              color: colors.graydark.primary,
            },
            '&::-moz-placeholder': {
              opacity: 1,
              color: colors.graydark.primary,
            },
            '&:-ms-input-placeholder': {
              opacity: 1,
              color: colors.graydark.primary,
            },
            '&::-webkit-input-placeholder': {
              opacity: 1,
              color: colors.graydark.primary,
            },
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '&.Mui-focused .MuiInputAdornment-positionEnd': {
            transform: 'rotate(180deg)',
          },
        },
        inputRoot: {
          padding: '1px !important',
          '.MuiInputAdornment-positionEnd': {
            top: 10,
            right: '12px !important',
          },
          '.MuiOutlinedInput-input': {
            cursor: 'pointer',
            borderRadius: '15px',
            padding: inputPadding,
            paddingRight: '48px !important',
          },
        },
        paper: {
          padding: '1px',
          boxShadow: 'none',
          borderRadius: '16px',
          background: gradients.accentGradient.primary.primary,
          '.MuiAutocomplete-listbox': {
            padding: 0,
          },
          '.MuiAutocomplete-noOptions': {
            padding: '10px 12px',
          },
          '.MuiAutocomplete-listbox, .MuiAutocomplete-noOptions': {
            width: '100%',
            borderRadius: '15px',
            background: colors.white.primary,
            '.MuiAutocomplete-option': optionStyles,
          },
        },
      },
    },

    // @ts-ignore
    MuiPickersOutlinedInput: {
      styleOverrides: {
        root: {
          padding: '1px',
          borderRadius: '16px',
          background: colors.gray.primary,

          '&.Mui-error': {
            background: colors.red.primary.primary,
          },

          [`& .${pickersOutlinedInputClasses.notchedOutline}`]: {
            display: 'none',
          },

          [`.${pickersOutlinedInputClasses.input}`]: {
            backgroundColor: colors.white.hover,
          },

          '&.Mui-focused, &.Mui-focused:hover': {
            background: gradients.accentGradient.primary.primary,
          },

          [`& .${pickersSectionListClasses.root}`]: {
            opacity: 'unset',
            borderRadius: '15px',
            padding: '10px 12px',
            background: colors.white.primary,
          },

          [`& .${pickersOutlinedInputClasses.input}`]: {
            height: '22px',
            borderRadius: '15px',
            padding: inputPadding,
            background: colors.white.primary,
          },

          '& .MuiInputAdornment-root': {
            margin: 0,
            top: '50%',
            padding: 0,
            right: '14px',
            position: 'absolute',
            transform: 'translateY(-50%)',
          },

          '&:hover:not(.Mui-focused)': {
            background: gradients.accentGradient.primary.primary,
            [`& .${pickersSectionListClasses.root}`]: {
              background: colors.white.primary,
            },
          },
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        input: {
          padding: inputPadding,
          '&.MuiSelect-select': {
            minHeight: '18px',
            lineHeight: '18px',
            borderRadius: '15px',
            paddingRight: '48px !important',
          },
        },
        root: {
          padding: '1px',
          borderRadius: '16px',
          background: colors.gray.primary,
          '&:hover': {
            background: gradients.accentGradient.primary.primary,
          },
          '&.MuiInputBase-root': {
            '& .MuiOutlinedInput-notchedOutline': {
              display: 'none',
            },
            '& input:-webkit-autofill': {
              WebkitTextFillColor: colors.black.primary,
              WebkitBoxShadow: `0 0 0 1000px ${colors.white.primary} inset`,
            },
            '&.Mui-error': {
              background: colors.red.primary.primary,

              '.MuiOutlinedInput-input': {
                backgroundColor: colors.white.primary,
              },
            },
            '&.Mui-focused': {
              background: gradients.accentGradient.primary.primary,

              '.MuiOutlinedInput-input': {
                backgroundColor: colors.white.primary,
              },
            },
            '&.MuiInputBase-adornedEnd': {
              '.MuiOutlinedInput-input': {
                paddingRight: 40,
              },
              '.MuiInputAdornment-positionEnd': {
                top: 8,
                right: 8,
                width: 24,
                height: 24,
                display: 'flex',
                position: 'absolute',
              },
            },
            '&.Mui-disabled': {
              opacity: 0.6,
              background: colors.gray.primary,
              '&:hover': {
                background: colors.gray.primary,
              },
              '&:hover .MuiOutlinedInput-input': {
                backgroundColor: colors.white.primary,
              },
              '.MuiOutlinedInput-input': {
                color: colors.graydark.primary,
                backgroundColor: colors.white.primary,
              },
            },
          },
        },
      },
    },
  },
});

export { colors, gradients };
export default theme;
